import { TaJson } from "ta-json";

import { Nullable, NullableResultPromise } from "../base-types";
import { ResponseHandler } from "../clients/response-handler";
import Guard from "../guard";
import { PermissionsResource } from "../models/permissions-resource";
import { UserEntityPermissionsResource } from "../models/user-entity-permissions-resource";
import { IExtendedContentHubClient } from "./extended-client";

export interface IPermissionsClient {
    /**
     * Gets the specified entity permissions.
     *
     * @param id - The id of the entity
     * @param userId - The id of the user
     */
    getPermissionsAsync(id: number, userId?: Nullable<number>): NullableResultPromise<Array<string>>;
}

export class PermissionsClient implements IPermissionsClient {
    protected readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        this._client = client;
    }

    async getPermissionsAsync(id: number, userId?: number): NullableResultPromise<Array<string>> {
        Guard.validId(id);

        let link;
        if (userId != null) {
            Guard.validId(userId);

            link = await this._client.linkHelper.explainUserPermissionsForEntityToLinkAsync(id, userId);
        } else {
            link = await this._client.linkHelper.permissionsForEntityToLinkAsync(id);
        }

        const response = await this._client.raw.getAsync(link.href);
        if (response.statusCode === 404) {
            return [];
        }

        ResponseHandler.handleErrors(response);

        if (userId != null) {
            const resource = TaJson.deserialize<UserEntityPermissionsResource>(
                response.content,
                UserEntityPermissionsResource
            );

            const permissions = resource.permissions.map(p => p.permission);
            return permissions != null && permissions.length > 0 ? permissions : [];
        } else {
            const resource = TaJson.deserialize<PermissionsResource>(response.content, PermissionsResource);

            return resource.permissions || [];
        }
    }
}
